import React, { useState, useRef } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  ShieldCheck, ArrowRight, ArrowLeft, Upload, Zap, Lock,
  CheckCircle2, Loader2, X,
} from "lucide-react";

/**
 * 3-step onboarding wizard for VeriFactu.
 *
 * Auto-appears (controlled by parent) when the company has no cert OR has
 * a cert but ``verifactu_enabled=false``. Walks the user through:
 *
 *   1) What VeriFactu is and why they need it.
 *   2) Upload the .p12 certificate + password (validates immediately).
 *   3) Flip the toggle to start sending invoices to AEAT (sandbox first).
 *
 * On step 2 success the wizard auto-advances to step 3. On step 3 success
 * we close the dialog and call ``onComplete`` so the parent reloads.
 */
export default function VerifactuOnboarding({ open, onClose, companyId, status, onComplete }) {
  const { lang } = useI18n();
  const t = (es, ca) => (lang === "es" ? es : ca);

  // If the user already has a cert, jump straight to step 3 (activation).
  const initialStep = status?.certificate ? 3 : 1;
  const [step, setStep] = useState(initialStep);
  const [password, setPassword] = useState("");
  const [uploading, setUploading] = useState(false);
  const [activating, setActivating] = useState(false);
  const fileRef = useRef(null);

  // Reset to initial state whenever the dialog opens
  React.useEffect(() => {
    if (open) {
      setStep(status?.certificate ? 3 : 1);
      setPassword("");
    }
  }, [open, status?.certificate]);

  const handleUpload = async () => {
    if (!fileRef.current?.files?.length) {
      toast.error(t("Selecciona el archivo .p12", "Selecciona el fitxer .p12"));
      return;
    }
    if (!password) {
      toast.error(t("Introduce la contraseña", "Introdueix la contrasenya"));
      return;
    }
    const fd = new FormData();
    fd.append("file", fileRef.current.files[0]);
    setUploading(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/verifactu/cert?password=${encodeURIComponent(password)}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(t("Certificado validado", "Certificat validat"));
      if (data.warning) toast.warning(data.warning, { duration: 7000 });
      setPassword("");
      setStep(3);
      // Defer parent reload until the wizard fully closes
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setUploading(false); }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      await api.put(`/companies/${companyId}/verifactu`, {
        enabled: true, environment: "test",
      });
      toast.success(t("VeriFactu activado (modo pruebas)", "VeriFactu activat (mode proves)"));
      onComplete?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setActivating(false); }
  };

  const skipForNow = () => {
    // Remember the skip for this session — parent decides via localStorage
    try {
      localStorage.setItem(
        `verifactu_onboarding_skipped_${companyId}`,
        String(Date.now())
      );
    } catch (_) { /* ignore quota */ }
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && skipForNow()}>
      <DialogContent
        className="bg-slate-900 border-white/10 max-w-xl"
        data-testid="verifactu-onboarding-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <ShieldCheck className="text-blue-400" size={18} />
            {t("Configura VeriFactu en 3 pasos", "Configura VeriFactu en 3 passos")}
          </DialogTitle>
        </DialogHeader>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-3 py-2"
             data-testid="onboarding-progress">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-3">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition ${
                  s < step ? "bg-emerald-500/80 text-white" :
                  s === step ? "bg-blue-500 text-white ring-4 ring-blue-500/20" :
                  "bg-white/10 text-slate-400"
                }`}
                data-testid={`onboarding-dot-${s}`}
              >
                {s < step ? <CheckCircle2 size={14} /> : s}
              </div>
              {s < 3 && (
                <div className={`w-10 h-0.5 ${s < step ? "bg-emerald-500/80" : "bg-white/10"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1 — Intro */}
        {step === 1 && (
          <div data-testid="onboarding-step-1" className="space-y-4 py-2">
            <h3 className="text-base font-semibold text-white">
              {t("¿Qué es VeriFactu?", "Què és VeriFactu?")}
            </h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              {t(
                "Es el sistema oficial de la AEAT que registra automáticamente cada factura emitida. Cada documento queda firmado y enviado en tiempo real a Hacienda — ya no hace falta presentar declaraciones manualmente.",
                "És el sistema oficial de l'AEAT que registra automàticament cada factura emesa. Cada document queda signat i enviat en temps real a Hisenda — ja no cal presentar declaracions manualment."
              )}
            </p>
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/[0.04] p-3 text-xs text-slate-300">
              <div className="flex items-center gap-1.5 text-blue-200 font-semibold mb-1">
                <Zap size={12} /> {t("Beneficios", "Beneficis")}
              </div>
              <ul className="space-y-1 ml-4 list-disc">
                <li>{t("Cumples con la normativa de forma automática", "Compleixes amb la normativa de forma automàtica")}</li>
                <li>{t("Reduce el riesgo de sanciones por errores manuales", "Redueix el risc de sancions per errors manuals")}</li>
                <li>{t("Tus clientes pueden validar tus facturas con un QR", "Els teus clients poden validar les teves factures amb un QR")}</li>
              </ul>
            </div>
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-3 text-xs text-amber-100">
              <strong>{t("Obligatorio desde:", "Obligatori des de:")}</strong>{" "}
              {t("01/07/2025 sociedades · 01/01/2026 autónomos", "01/07/2025 societats · 01/01/2026 autònoms")}
            </div>
            <div className="text-xs text-slate-400">
              {t(
                "Necesitas tu certificado digital en formato .p12 o .pfx (FNMT, Camerfirma, etc.). Si aún no lo tienes, obtenlo en:",
                "Necessites el teu certificat digital en format .p12 o .pfx (FNMT, Camerfirma, etc.). Si encara no el tens, obtén-lo a:"
              )}{" "}
              <a href="https://www.sede.fnmt.gob.es/certificados" target="_blank" rel="noreferrer"
                 className="text-blue-300 hover:text-blue-200 underline">
                sede.fnmt.gob.es
              </a>
            </div>
            <div className="flex items-center justify-between pt-3">
              <button type="button" onClick={skipForNow}
                      className="text-xs text-slate-500 hover:text-slate-300"
                      data-testid="onboarding-skip-1">
                {t("Más tarde", "Més tard")}
              </button>
              <Button onClick={() => setStep(2)}
                      data-testid="onboarding-next-1"
                      className="bg-blue-600 hover:bg-blue-700">
                {t("Tengo mi certificado", "Ja tinc el certificat")}
                <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 — Upload */}
        {step === 2 && (
          <div data-testid="onboarding-step-2" className="space-y-4 py-2">
            <h3 className="text-base font-semibold text-white">
              {t("Sube tu certificado", "Puja el teu certificat")}
            </h3>
            <p className="text-sm text-slate-400">
              {t(
                "Tu clave privada se cifra con AES-128 antes de guardarse. Nadie más que el servidor de tu empresa puede leerla.",
                "La teva clau privada es xifra amb AES-128 abans de desar-se. Ningú més que el servidor de la teva empresa la pot llegir."
              )}
            </p>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">
                {t("Archivo .p12 / .pfx", "Fitxer .p12 / .pfx")}
              </label>
              <input
                ref={fileRef}
                type="file"
                accept=".p12,.pfx,application/x-pkcs12"
                className="block w-full text-sm text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-500/20 file:text-blue-200 hover:file:bg-blue-500/30"
                data-testid="onboarding-file"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
                <Lock size={11} /> {t("Contraseña", "Contrasenya")}
              </label>
              <Input
                type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-white/5 border-white/10"
                data-testid="onboarding-password"
              />
            </div>
            <div className="flex items-center justify-between pt-2">
              <button type="button" onClick={() => setStep(1)}
                      className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1">
                <ArrowLeft size={12} /> {t("Atrás", "Enrere")}
              </button>
              <Button onClick={handleUpload} disabled={uploading}
                      data-testid="onboarding-upload-btn"
                      className="bg-blue-600 hover:bg-blue-700">
                {uploading
                  ? <Loader2 className="mr-1.5 animate-spin" size={14} />
                  : <Upload size={14} className="mr-1.5" />}
                {t("Subir y validar", "Pujar i validar")}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 — Activate */}
        {step === 3 && (
          <div data-testid="onboarding-step-3" className="space-y-4 py-2">
            <h3 className="text-base font-semibold text-white">
              {t("Último paso: activa el envío", "Últim pas: activa l'enviament")}
            </h3>
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-3 text-xs text-emerald-100 flex items-start gap-2">
              <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" />
              <div>
                {t(
                  "Tu certificado ya está guardado de forma segura. Activando el envío real, cada factura emitida se enviará automáticamente a AEAT en modo PRUEBAS (preproducción).",
                  "El teu certificat ja està desat de forma segura. Activant l'enviament real, cada factura emesa s'enviarà automàticament a AEAT en mode PROVES (preproducció)."
                )}
              </div>
            </div>
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-3 text-xs text-amber-100">
              <strong>{t("Modo pruebas activado por defecto.", "Mode proves activat per defecte.")}</strong>
              {" "}
              {t(
                "Cuando estés listo, podrás cambiar a PRODUCCIÓN desde Configuración → VeriFactu.",
                "Quan estiguis preparat, podràs canviar a PRODUCCIÓ des de Configuració → VeriFactu."
              )}
            </div>
            <div className="flex items-center justify-between pt-2">
              <button type="button" onClick={skipForNow}
                      className="text-xs text-slate-500 hover:text-slate-300"
                      data-testid="onboarding-skip-3">
                {t("Activar más tarde", "Activar més tard")}
              </button>
              <Button onClick={handleActivate} disabled={activating}
                      data-testid="onboarding-activate-btn"
                      className="bg-emerald-600 hover:bg-emerald-700">
                {activating
                  ? <Loader2 className="mr-1.5 animate-spin" size={14} />
                  : <Zap size={14} className="mr-1.5" />}
                {t("Activar VeriFactu", "Activar VeriFactu")}
              </Button>
            </div>
          </div>
        )}

        {/* Small dismiss button (top-right) */}
        <button type="button" onClick={skipForNow}
                aria-label="close"
                className="absolute top-3 right-3 text-slate-500 hover:text-slate-300"
                data-testid="onboarding-close-btn">
          <X size={16} />
        </button>
      </DialogContent>
    </Dialog>
  );
}
